import React, { useEffect, useRef, useState, useContext, useMemo } from "react";
import { connect } from "react-redux";
import { assignCandidate } from "requests/caseAppointment";
import { AppointmentAssignmentCandidate, DayOfWeek, AppointmentCandidateSubmission, SimpleResponse } from "interfaces/Mueller.Business.Models";
import { MSpinner, MTable } from "components/Shared";
import { SecondaryStatus } from "typeConstants/caseStatusCodes";
import { DialogContext, DialogProviderValue } from "components/Shared/DialogProvider";
import { Button } from "react-md";
import { InitialState } from "reducers/initialState";
import { getCandidates, clearCandidates, setControlNumber } from "actions/caseAppointment/index.js";
import AppointmentConfirmation from "./AppointmentConfirmation";

export const validStatusesForAppointmentGrid = [ SecondaryStatus.PREASSIGNMENT ];

interface AppointmentGridProps {
  controlNumber: number;
  refreshParent: () => void | null;  // Optional function to call after assigning to refresh parent component
  // Redux
  candidates: AppointmentAssignmentCandidate[];
  endDate: Date | undefined;
  isFetchingCandidates: boolean;
  fetchingCandidatesError: boolean;
  getCandidates: (controlNumber: number, callback: () => void) => void;
  clearCandidates: () => void;
  candidatesControlNumber: number;
  setCandidatesControlNumber: (controlNumber: number) => void;
  summary?: string[] | string | null;
}

const AppointmentGrid = (props: AppointmentGridProps) => {
  const [hasRequestedCandidates, setHasRequestedCandidates] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showSummary, setShowSummary] = useState<boolean>(false);

  const dialog = useContext<DialogProviderValue>(DialogContext);
  const mounted = useRef<boolean>(true);

  useEffect(() => {
    if (props.candidatesControlNumber && (props.candidatesControlNumber !== props.controlNumber)) {
      props.clearCandidates();
      props.setCandidatesControlNumber(props.controlNumber);
    } else if (!props.candidatesControlNumber) {
      props.setCandidatesControlNumber(props.controlNumber);
    }
    return () => {
      mounted.current = false;
    };
  }, []);
  
  // Candidate Search

  const onSearch = () => {
    setIsLoading(true);
    const callback = () => {
      setHasRequestedCandidates(true);
      setIsLoading(false);
    };
    props.getCandidates(props.controlNumber, callback);
  };

  // --- Summary (candidate evaluation log) ---

  const summaryLines = useMemo(() => {
    const s = props.summary;
    if (!s) return [];

    if (Array.isArray(s)) {
      return s.map(l => (l ?? "").toString()).filter(l => l.length > 0);
    }

    return s
      .toString()
      .split(/\r?\n/g)
      .filter(l => l.length > 0);
  }, [props.summary]);

  const renderSummary = () => {
    if (props.candidates?.length) return null; 
    if (summaryLines?.length === 0) return null;

    const tableData = summaryLines.map((line, idx) => ({
      id: idx,
      line,
    }));

    return (
      <div style={{ marginTop: 16 }}>
        <Button
          raised
          primary
          onClick={() => setShowSummary(prev => !prev)}
          aria-expanded={showSummary}
        >
          {showSummary ? "Hide candidate evaluation log" : "Show candidate evaluation log"}
        </Button>

        {showSummary && (
          <div style={{ marginTop: 8, maxHeight: 260, overflow: "auto" }}>
            <MTable
              className="appointment-grid-summary-table"
              columnDefinitions={[
                {
                  name: "Candidate evaluation log",
                  dataSource: "line",
                  style: {
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    fontFamily: "monospace",
                    fontSize: 12,
                  },
                },
              ]}
              data={tableData}
              emptyButtonText={"No log output"}
            />
          </div>
        )}
      </div>
    );
  };

  // Dialog

  const onCandidateSelect = (candidate: AppointmentAssignmentCandidate) => {
    const onAssign = (contacts: string[]) => {
      let submission: AppointmentCandidateSubmission = {
        fieldRepNumber: candidate.fieldRepNumber,
        //@ts-ignore
        appointmentDateTime: candidate.appointmentDateTime,
        confirmationMessageRecipients: contacts ?? [],
      };

      const onSuccess = (response: SimpleResponse) => {
        setIsLoading(false);
        if (response.error) {
          dialog.showOk("Error", response.errorMessages.join("\r\n"), () => dialog.hide());
        }
        else if (props.refreshParent) {
          // Just guessing at how long the back end takes to assign the case
          setTimeout(() => props.refreshParent(), 1000);
        }
      };
      const onError = () => {
        setIsLoading(false);
      };
      
      dialog.hide();
      setIsLoading(true);
      assignCandidate(props.controlNumber, submission, onSuccess, onError, mounted);
    };
    const onCancel = () => dialog.hide();
    
    const dialogProps = { className: "assign-appointment-dialog"};

    dialog.show(
      "Assign & Send Appointment Confirmation",
      <AppointmentConfirmation 
        onSave={onAssign}
        onCancel={onCancel}
        saveButtonLabel="Assign"
      />,
      null,
      dialogProps
    );
  };

 

  // Grid settings 
  const dayMilliseconds = 24 * 60 * 60 * 1000; // hours*minutes*seconds*milliseconds
  const today = new Date();
  const end = props.endDate ? new Date(props.endDate) : null;
  const daysFromToday = end
    // @ts-ignore dates work here, but TS doesn't think so
    ? Math.round(Math.abs((end - today) / dayMilliseconds))
    : 10; // If we don't have an end date, we shouldn't have candidates but this is a reasonable default
  const MORNING = "MORNING";
  const AFTERNOON = "AFTERNOON";
  const getDayColumns = () => {
    const columns = [today];
    // Start index at 1 since we already have current date 
    for (let i = 1; i < daysFromToday; i++) {
      let newDate = new Date(columns[i - 1]);
      newDate.setDate(newDate.getDate() + 1);
      columns.push(newDate);
    }
    return columns;
  };
  const dayColumns = getDayColumns();

  const getCandidateForSlot = (date: Date, timeSlot: string) => {
    let candidate: AppointmentAssignmentCandidate = null;
    const potentialCandidates = props.candidates.filter(c => new Date(c.appointmentDateTime).toDateString() === date.toDateString());
    potentialCandidates.forEach(c => {
      switch (timeSlot) {
        case MORNING:
          if (new Date(c.appointmentDateTime).getHours() < 13) {
            candidate = c;
          }
          break;
        case AFTERNOON:
          if (new Date(c.appointmentDateTime).getHours() >= 13) {
            candidate = c;
          }
          break;
      }
    });
    return candidate;
  };

  const getAbbreviatedDayOfWeek = (dayIndex: number) => {
    return DayOfWeek[dayIndex].substring(0, 3);
  };

  // Grid styles
  const headerCell = "appointment-grid-header-cell";
  const unavailableCell = "appointment-grid-unavailable-cell";
  const availableCell = "appointment-grid-available-cell";
  const appointmentGridRow = "appointment-grid-row";
  const rankCallout = "appointment-grid-rank-callout";
  const gridRightEdge = "appointment-grid-right-edge";
  const gridBottonEdge = "appointment-grid-bottom-edge";

  // Render

  if (isLoading) return (<MSpinner />);

  if (hasRequestedCandidates && props.fetchingCandidatesError) return (
    <div>
      <p>Unable to retrieve available appointment times</p>
    </div>
  );

  if (hasRequestedCandidates && !props.candidates.length) return (
    <div>
      <p>No available appointment times</p>
      {renderSummary()}
    </div>
  );

  if (!hasRequestedCandidates && !props.candidates.length) return (
    <div>
      <Button
        raised
        primary
        onClick={onSearch}
      >Run Search</Button>

      {renderSummary()}
    </div>
  );

  const getCellForSlot = (date: Date, timeSlot: string, index: number) => {
    let candidate = getCandidateForSlot(date, timeSlot);
    if (candidate) {
      return (
        <div 
          key={`${date.toUTCString()}+${candidate.fieldRepNumber}`}
          className={`${availableCell} ${index === (dayColumns.length - 1) ? gridRightEdge : ""}`}
          onClick={() => onCandidateSelect(candidate)}
        >
          <div className={rankCallout}>{candidate.rank}</div>
          <p>{candidate.distanceToCase.toFixed(2)} mi</p>
        </div>);
    } else {
      return (<div className={`${unavailableCell} ${index === (dayColumns.length - 1) ? gridRightEdge : ""}`} key={`${date.toUTCString()}+${timeSlot}`} />);
    }
  };

  return (
    <div className="appointment-grid-container">
      {/* Header Row */}
      <div className={appointmentGridRow}>
        {/* Empty cell to start grid */}
        <div className={`${headerCell} ${gridRightEdge} ${gridBottonEdge}`} />
        {/* Column headers */}
        {dayColumns.map(d => {
          return (
            <div className={`${headerCell} ${gridRightEdge} ${gridBottonEdge}`} key={d.toUTCString()}>
              <div>{getAbbreviatedDayOfWeek(d.getDay())}</div>
              <div>{d.getDate()}</div>
            </div>);
        })}
      </div>

      {/* Morning Row */}
      <div className={appointmentGridRow}>
        {/* Row headers */}
        <div className={`${headerCell} ${gridRightEdge} ${gridBottonEdge}`}>8am - 1pm</div>
        {/* Morning Grid Cells */}
        {dayColumns.map((d, i) => getCellForSlot(d, MORNING, i))}
      </div>

      {/* Afternoon Row */}
      <div className={appointmentGridRow}>
        {/* Left side header */}
        <div className={`${headerCell} ${gridRightEdge} ${gridBottonEdge}`}>1pm - 6pm</div>
        {/* Afternoon Grid Cells */}
        {dayColumns.map((d, i) => getCellForSlot(d, AFTERNOON, i))}
      </div>

      {renderSummary()}
    </div>
  );
};

const mapStateToProps = (state: InitialState) => ({
  candidates: state.caseAppointment.candidates,
  endDate: state.caseAppointment.endDate,
  isFetchingCandidates: state.caseAppointment.fetchingCandidates,
  fetchingCandidatesError: state.caseAppointment.fetchingCandidatesError,
  candidatesControlNumber: state.caseAppointment.controlNumber,
  summary: state.caseAppointment.summary,
});

const mapDispatchToProps = dispatch => ({
  getCandidates(controlNumber: number, callback: () => void) {
    dispatch(getCandidates(controlNumber, callback));
  },
  clearCandidates() {
    dispatch(clearCandidates());
  },
  setCandidatesControlNumber(controlNumber: number) {
    dispatch(setControlNumber(controlNumber));
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(AppointmentGrid);